// The catalogues are imported statically so that every consumer (Workers, PDF renderer, React
// apps) gets them through its bundler; nothing reads from disk at runtime.

import consoleDe from '../catalogs/console/de.json' with { type: 'json' }
import consoleEn from '../catalogs/console/en.json' with { type: 'json' }
import consoleNl from '../catalogs/console/nl.json' with { type: 'json' }
import invoiceDe from '../catalogs/invoice/de.json' with { type: 'json' }
import invoiceEn from '../catalogs/invoice/en.json' with { type: 'json' }
import invoiceNl from '../catalogs/invoice/nl.json' with { type: 'json' }
import notifyDe from '../catalogs/notify/de.json' with { type: 'json' }
import notifyEn from '../catalogs/notify/en.json' with { type: 'json' }
import notifyNl from '../catalogs/notify/nl.json' with { type: 'json' }
import pdfDe from '../catalogs/pdf/de.json' with { type: 'json' }
import pdfEn from '../catalogs/pdf/en.json' with { type: 'json' }
import pdfFr from '../catalogs/pdf/fr.json' with { type: 'json' }
import pdfNl from '../catalogs/pdf/nl.json' with { type: 'json' }
import requirementsDe from '../catalogs/requirements/de.json' with { type: 'json' }
import requirementsEn from '../catalogs/requirements/en.json' with { type: 'json' }
import requirementsNl from '../catalogs/requirements/nl.json' with { type: 'json' }
import rulesDe from '../catalogs/rules/de.json' with { type: 'json' }
import rulesEn from '../catalogs/rules/en.json' with { type: 'json' }
import rulesNl from '../catalogs/rules/nl.json' with { type: 'json' }
import signDe from '../catalogs/sign/de.json' with { type: 'json' }
import signEn from '../catalogs/sign/en.json' with { type: 'json' }
import signFr from '../catalogs/sign/fr.json' with { type: 'json' }
import signNl from '../catalogs/sign/nl.json' with { type: 'json' }
import type { Catalogs } from './generated/types'
import type { CatalogLocale, Namespace } from './namespaces'

export type Catalogue = Readonly<Record<string, string>>

/**
 * The source catalogues, typed against the generated key map. Editing a `nl` catalogue without
 * running `pnpm --filter @freightapi/i18n gen-types` makes this assignment fail to compile.
 */
const SOURCE_CATALOGUES: { readonly [N in keyof Catalogs]: Catalogs[N] } = {
  console: consoleNl,
  invoice: invoiceNl,
  notify: notifyNl,
  pdf: pdfNl,
  requirements: requirementsNl,
  rules: rulesNl,
  sign: signNl,
}

/**
 * Keyed on the generated key map rather than on {@link Namespace}, so a catalogue directory that is
 * not registered in `namespaces.ts` (and the other way around) fails to compile.
 */
const CATALOGUES: {
  readonly [N in keyof Catalogs]: Readonly<Partial<Record<CatalogLocale, Catalogue>>>
} = {
  console: { nl: SOURCE_CATALOGUES.console, en: consoleEn, de: consoleDe },
  invoice: { nl: SOURCE_CATALOGUES.invoice, en: invoiceEn, de: invoiceDe },
  notify: { nl: SOURCE_CATALOGUES.notify, en: notifyEn, de: notifyDe },
  pdf: { nl: SOURCE_CATALOGUES.pdf, en: pdfEn, de: pdfDe, fr: pdfFr },
  requirements: { nl: SOURCE_CATALOGUES.requirements, en: requirementsEn, de: requirementsDe },
  rules: { nl: SOURCE_CATALOGUES.rules, en: rulesEn, de: rulesDe },
  sign: { nl: SOURCE_CATALOGUES.sign, en: signEn, de: signDe, fr: signFr },
}

/**
 * The catalogue for one namespace and locale, or `undefined` when that combination has no file.
 * Optional chaining on the namespace keeps unvalidated runtime input from throwing; the types
 * already rule it out.
 */
export function catalogueFor(namespace: Namespace, locale: CatalogLocale): Catalogue | undefined {
  return CATALOGUES[namespace]?.[locale]
}
